import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Check, Lock, X, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDON_LABEL = {
  social_media: 'Social Media',
  paid_ads: 'Paid Ads',
  ai_video: 'AI Video',
  website_creation: 'Website Creation',
  legal_translations: 'Legal Translations'
};

export default function AddonUnlockModal({ open, addonKey, onClose, onUnlocked }) {
  const [plans, setPlans] = useState(null);
  const [selecting, setSelecting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !addonKey) return;
    setError('');
    const token = localStorage.getItem('token');
    axios.get(`${API}/addons/pricing/${addonKey}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPlans(r.data.plans))
      .catch(e => setError(e.response?.data?.detail || 'Could not load pricing'));
  }, [open, addonKey]);

  // Lock body scroll + close on Esc while modal is open
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const handleUnlock = async (planKey) => {
    setLoading(true);
    setError('');
    setSelecting(planKey);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/addons/unlock`,
        { addon_key: addonKey, plan: planKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUnlocked && onUnlocked(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not unlock add-on. Please try again.');
    } finally {
      setLoading(false);
      setSelecting(null);
    }
  };

  if (!open) return null;
  const planOrder = ['starter', 'growth', 'pro'];

  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999, background: 'rgba(3, 7, 18, 0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      onClick={onClose}
      data-testid="addon-unlock-modal"
    >
      <div
        className="relative w-full max-w-5xl bg-gradient-to-b from-slate-900 to-slate-950 border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        style={{ zIndex: 10000 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition"
          data-testid="addon-unlock-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="p-8 border-b border-white/10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/15 border border-violet-500/25 text-violet-300 text-xs font-semibold uppercase tracking-wider mb-3">
            <Lock className="w-3.5 h-3.5" /> Locked add-on
          </div>
          <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit' }} data-testid="addon-unlock-title">
            Unlock {ADDON_LABEL[addonKey] || addonKey}
          </h2>
          <p className="text-sm text-slate-400 mt-2 max-w-2xl">
            Choose a plan that suits your firm's growth ambition. You can upgrade or change tiers later from your dashboard.
          </p>
        </div>

        {error && (
          <div className="m-6 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="addon-unlock-error">
            {error}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5 p-8">
          {plans && planOrder.map((key, idx) => {
            const plan = plans[key];
            if (!plan) return null;
            const featured = key === 'growth';
            return (
              <div
                key={key}
                className={`relative flex flex-col rounded-2xl p-6 border transition-all ${
                  featured
                    ? 'bg-gradient-to-b from-violet-900/30 to-indigo-900/20 border-violet-500/40 shadow-xl shadow-violet-900/30'
                    : 'bg-slate-800/40 border-white/5 hover:border-white/15'
                }`}
                data-testid={`addon-plan-${key}`}
              >
                {featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">
                    Most Popular
                  </div>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-violet-300" />
                  </div>
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{plan.label}</h3>
                </div>
                <div className="text-2xl font-bold text-white mb-4">{plan.price}</div>
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleUnlock(key)}
                  disabled={loading}
                  className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 ${
                    featured
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-xl hover:shadow-violet-600/40'
                      : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`addon-plan-unlock-${key}`}
                >
                  {selecting === key ? 'Unlocking…' : 'Unlock Now'}
                </button>
              </div>
            );
          })}
          {!plans && (
            <div className="col-span-3 text-center text-sm text-slate-400 py-12">Loading pricing…</div>
          )}
        </div>

        <div className="px-8 pb-6 -mt-2">
          <p className="text-[11px] text-slate-500">Demo unlock - no payment is collected. Plan tier is recorded against your firm and the add-on is enabled instantly.</p>
        </div>
      </div>
    </div>
  );

  // Render via portal to document.body to escape any ancestor stacking context
  // (sidebar uses sticky positioning; dashboard content wrappers may use transform/overflow which would otherwise clip us).
  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}
